import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { GiCrossedBones } from "react-icons/gi";
import { LuSave } from "react-icons/lu";
import { Mail, Trash2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { RiFileExcel2Line } from "react-icons/ri";
import { Checkbox } from "@chakra-ui/react";
import { AgCheckbox } from "ag-grid-community";
const ConsolidatedRenewalList = () => {
    const navigate = useNavigate();
    const [members, setMembers] = useState([
        {
            quotationNo: "",
            policyHolder: "",
            expiryDate: "",
            product: "",
            sum: "",
            renewalPremium: "",
        }
    ]);

    const validationSchema = Yup.object({
        renewalFrom: Yup.string().required("Renewal period 'From' is required"),
        renewalTo: Yup.string().required("Renewal period 'To' is required"),
        paymentMode: Yup.string().required("Payment mode is required"),
        productFilter: Yup.string().required("Product is required"),
        members: Yup.array()
            .of(
                Yup.object({
                    quotationNo: Yup.string().required("Quotation number is required"),
                    policyHolder: Yup.string().required("Policy holder is required"),
                    expiryDate: Yup.string().required("Expiry date is required"),
                    product: Yup.string().required("Product is required"),
                    sum: Yup.string().required("Sum is required"),
                    renewalPremium: Yup.string().required("Renewal premium is required"),
                })
            )
            .min(1, "At least one member is required"),
    });

    const handleAddMember = () => {
        setMembers([...members, { quotationNo: "", policyHolder: "", expiryDate: "", product: "", sum: "", renewalPremium: "" }]);
    };

    const handleRemoveMember = (index) => {
        const newMembers = [...members];
        newMembers.splice(index, 1);
        setMembers(newMembers);
    };

    const handleMemberChange = (index, field, value) => {
        const newMembers = [...members];
        newMembers[index][field] = value;
        setMembers(newMembers);
    };

    const handleSubmit = (values, { resetForm }) => {
        const formData = {
            ...values,
            members: members,
        };
        console.log("Submitted:", formData);
        alert("Form Submitted!");
        resetForm();
        setMembers([]);
    };

    return (
        <div className="formContainer lg:p-4 rounded" style={{ boxShadow: "0 0 8px #ccc" }}>
            <Formik
                initialValues={{
                    renewalFrom: "",
                    renewalTo: "",
                    paymentMode: "",
                    productFilter: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, isSubmitting, setFieldValue }) => (
                    <Form className="p-4 bg-white rounded">
                        <div className="flex justify-between items-center border-b border-brandRed pb-2 mb-4">
                            {/* Left side - Text */}
                            <div>
                                <h5 className="text-brandRed font-bold">Consolidated Renewal List</h5>
                            </div>

                            {/* Right side - Icons */}
                            <div className="flex gap-3">
                                <div><Mail size={30}/></div>
                                <div><FaWhatsapp size={30} /></div>
                                <div><RiFileExcel2Line size={30} /></div>
                            </div>
                        </div>

                        <div className="row g-3 mb-4 align-items-end">

                            {/* Renewal Period Label - centered over From & To */}
                            <div className="col-lg-6 text-center">
                                <label className="fw-bold">Renewal Period</label>
                            </div>
                            <div className="col-lg-3 text-start">
                                <label className="form-label">Payment Mode</label>
                            </div>
                            <div className="col-lg-3 text-start">
                                <label className="form-label">Product</label>
                            </div>

                            {/* From + To fields side-by-side with inline labels and calendar icons */}
                            <div className="col-lg-3 d-flex align-items-center">
                                <label className="form-label me-2">From</label>
                                <div className="position-relative w-100">
                                    <Field
                                        type="date"
                                        name="renewalFrom"
                                        className="form-control custom-shadow"
                                    />
                                </div>
                            </div>

                            <div className="col-lg-3 d-flex align-items-center">
                                <label className="form-label me-2">To</label>
                                <div className="position-relative w-100">
                                    <Field
                                        type="date"
                                        name="renewalTo"
                                        className="form-control custom-shadow"
                                    />
                                </div>
                            </div>

                            {/* Payment Mode */}
                            <div className="col-lg-3">
                                <Field as="select" name="paymentMode" className="form-select custom-shadow">
                                    <option value="">Select</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Credit Card">Credit Card</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                </Field>
                                <ErrorMessage name="paymentMode" component="div" className="text-danger" />
                            </div>

                            {/* Product */}
                            <div className="col-lg-3">
                                <Field as="select" name="productFilter" className="form-select custom-shadow">
                                    <option value="">Select</option>
                                    <option value="Health Suraksha">Health Suraksha</option>
                                    <option value="MediCare">MediCare</option>
                                </Field>
                                <ErrorMessage name="productFilter" component="div" className="text-danger" />
                            </div>
                        </div>

                        <div className="flex justify-center p-4">
                            <button type="submit" className="backButton flex justify-center mb-3">Generate Renewal</button>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th className="form-label text-truncate" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}></th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Quotation No">Quotation No</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Policy Holder">Policy Holder</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Expiry Date">Expiry Date</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Product">Product</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Sum">Sum</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Renewal Premium">Renewal Premium</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member, index) => (
                                        <tr key={index}>
                                            <td>  <input type="checkbox" /> </td>
                                            <td>  </td>
                                            <td>  </td>
                                            <td> </td>
                                            <td> </td>
                                            <td> </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default ConsolidatedRenewalList;